import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  postBusinessOperatingHours,
  getBusinesOperatingHours,
} from "../../../../services/Handlers/BusinessProfileService";
import { useAuth } from "../../../../context/AuthProvider";
import OperationalHoursComponents from "../components";
import { useNotification } from "../../../../context/NotificationProvider";

export default function OperationalHours() {
  const { userDetail } = useAuth();
  const { error, success } = useNotification();
  const [operatingHours, setOperatingHours] = useState([]);

  // Generate time options (24-hour format)
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        times.push({
          value: timeString,
          label: timeString,
        });
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  // Fetch operating hours
  const { isLoading, refetch } = useQuery(
    ["BusinessOperatingHours", userDetail?.businessId],
    () => getBusinesOperatingHours(userDetail?.businessId),
    {
      enabled: !!userDetail?.businessId,
      onSuccess: (res) => {
        if (res.status === 200) {
          if (res?.data?.data) {
            setOperatingHours(res.data.data.operating_hours);
          }
        }
      },
      onError: (error) => {
        console.error("Failed to fetch operating hours:", error);
        error("Gagal memuat jam operasional");
      },
    }
  );

  // Save operating hours mutation
  const { mutate: saveOperatingHours, isLoading: isSaving } = useMutation(
    (data) => postBusinessOperatingHours(data),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          success("Jam operasional berhasil disimpan");
          refetch();
        }
      },
      onError: (error) => {
        console.error("Failed to save operating hours:", error);
        error("Gagal menyimpan jam operasional");
      },
    }
  );

  // Handle day operating hours change
  const handleDayChange = (dayOfWeek, field, value) => {
    setOperatingHours((prev) =>
      prev.map((day) => {
        if (day.day_of_week === dayOfWeek) {
          const updatedDay = { ...day, [field]: value };

          // If closing the day, clear times
          if (field === "is_closed" && value === true) {
            updatedDay.opening_time = null;
            updatedDay.closing_time = null;
          }

          return updatedDay;
        }
        return day;
      })
    );
  };

  // Handle save
  const handleSave = () => {
    if (!userDetail?.businessId) {
      error("Business ID tidak ditemukan");
      return;
    }

    // Validate operating hours
    const hasInvalidHours = operatingHours.some((day) => {
      if (!day.is_closed) {
        if (!day.opening_time || !day.closing_time) {
          return true;
        }
        // Check if opening time is not after closing time
        if (day.opening_time >= day.closing_time) {
          return true;
        }
      }
      return false;
    });

    if (hasInvalidHours) {
      error(
        "Periksa kembali jam operasional. Pastikan jam buka lebih awal dari jam tutup."
      );
      return;
    }

    const payload = {
      business_id: parseInt(userDetail.businessId),
      operating_hours: operatingHours.map((day) => ({
        day_of_week: day.day_of_week,
        opening_time: day.is_closed ? null : day.opening_time,
        closing_time: day.is_closed ? null : day.closing_time,
        is_closed: day.is_closed,
      })),
    };

    saveOperatingHours(payload);
  };

  // Handle copy hours to all days
  const handleCopyToAll = (sourceDay) => {
    const source = operatingHours.find((day) => day.day_of_week === sourceDay);
    if (!source) return;

    setOperatingHours((prev) =>
      prev.map((day) => ({
        ...day,
        opening_time: source.opening_time,
        closing_time: source.closing_time,
        is_closed: source.is_closed,
      }))
    );
  };

  return (
    <OperationalHoursComponents
      operatingHours={operatingHours}
      timeOptions={timeOptions}
      onDayChange={handleDayChange}
      onSave={handleSave}
      onCopyToAll={handleCopyToAll}
      isLoading={isLoading}
      isSaving={isSaving}
    />
  );
}
